// packages/web-ui/src/components/inline-cards/IllustrativeImagePanel.tsx
//
// IV3-S2 / IV3-S3 — the non-editable LLM-raster card body. It renders BELOW the
// deterministic React-Flow sketch card (`ResponseViewProposalCard`) for the same
// view, so the AI image is an ADDITIONAL representation stacked under the faithful
// diagram — never a tab that replaces it. The diagram + the raw `view.sysml`
// source-of-truth therefore live on the sketch card above; this card is purely
// the raster + its natural-language edit loop:
//   - a role=status, aria-live skeleton + ELAPSED TIMER + Cancel while
//     generating (the displayed prior image, if any, stays visible under the
//     overlay — chat is never blocked);
//   - the resolved raster `<img>` (never editable) with a NON-DISMISSIBLE
//     "illustrative" badge + click-to-expand lightbox;
//   - the natural-language CHANGE BOX (cost-first, explicit-invoke-only) with
//     a versioned iterate-history strip;
//   - an explicit "the model declined this edit" message for
//     IMAGE_CONTENT_FILTERED (distinct from a silent fallback) with Retry.
//
// The card makes NO direct network call — the NL POST flows through the
// injected `onRequestImageEdit` handler (default delegates to the views image
// route; tests inject a mock). The produced image lands asynchronously via the
// `image:genReady` SSE event → `image-views.resolveReady`.

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';
import { CardShell } from './CardShell.js';
import { ViewImageModal, ILLUSTRATIVE_BADGE_COPY } from './ViewImageModal.js';
import {
  GeneratingViewState,
  GENERATING_CANVAS_WIDTH,
  GENERATING_CANVAS_HEIGHT,
} from '../loading/GeneratingViewState.js';
import { IMAGE_GEN_TIMEOUT_MS } from './raster-timeout.js';
import type { IllustrativeImageEnvelopeData } from '../../state/inline-cards.js';
import {
  selectImageCard,
  type ImageViewsSlice,
} from '../../state/image-views.js';

/** Rough per-image cost estimate shown BEFORE the NL-box call (IV3-S3). */
export const IMAGE_EDIT_COST_ESTIMATE_USD = 0.05;
/** Latency hint shown before the call. */
export const IMAGE_EDIT_LATENCY_HINT = '~30–120s';

/**
 * MAJ-3 — client-side timeout backstop. When neither `image:genReady` nor
 * `image:genFailed` lands within this window, the panel stops spinning and
 * shows a "taking longer than expected / fell back" state so the card can
 * NEVER hang in an infinite spinner (e.g. a dropped SSE connection or a
 * server that crashed mid-generation). 180s comfortably exceeds the worst-case
 * raster latency hint (~120s) plus network slack.
 *
 * The value now lives in `./raster-timeout.js` (a leaf module) so every raster
 * surface shares one constant without an import cycle; this re-export keeps the
 * long-standing `IMAGE_GEN_TIMEOUT_MS` import path off this module stable
 * (imported at the top of this file, re-exported here).
 */
export { IMAGE_GEN_TIMEOUT_MS };

/**
 * Structural-change heuristic (IV3-S3): when the instruction reads like a model
 * edit (add/remove/connect/wire/delete + a part/port/connection noun) the panel
 * nudges the user toward `propose_kb_changeset` — the picture box restyles, it
 * does not mutate the model.
 */
const STRUCTURAL_VERBS = /\b(add|remove|delete|connect|wire|disconnect|insert|create)\b/i;
const STRUCTURAL_NOUNS = /\b(part|parts|port|ports|connection|connections|component|subsystem|interface)\b/i;

export function isStructuralChangeRequest(instruction: string): boolean {
  return STRUCTURAL_VERBS.test(instruction) && STRUCTURAL_NOUNS.test(instruction);
}

export interface IllustrativeImagePanelHandlers {
  /** Live image-views store the panel subscribes to (keyed by imageViewId). */
  store: StoreApi<ImageViewsSlice>;
  /**
   * Fire the NL change box POST (202 accept). The result resolves the accept
   * only — the produced image lands later via `image:genReady`.
   *
   * MAJ-13 — the FIRST argument is the stable `imageViewId` (the card
   * correlation id), NOT the raw `viewId`. The handler routes through the
   * image-views slice's `requestEdit(imageViewId, instruction)`, which keys
   * the optimistic 'generating' overlay + version lineage by `imageViewId`
   * and POSTs to the card's resolved `viewId`. Routing by `imageViewId`
   * (not `viewId`) is what lets the skeleton overlay + history engage.
   */
  onRequestImageEdit: (
    imageViewId: string,
    instruction: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * Open the raw `view.sysml` source of truth (one-action affordance). The
   * source-of-truth pillar primarily lives on the sibling sketch card's "SysML
   * source" tab; this handler is retained for callers that also want an
   * external-open affordance from the raster card. Currently unused by the
   * image-only body but kept on the contract for back-compat.
   */
  onOpenSysmlSource: (viewId: string, sysmlSourcePath?: string) => void;
  /** Optional PNG export. */
  onExportPng?: (viewId: string) => void;
  /**
   * Fetch the DETERMINISTIC ReactFlow layout JSON for the view. Retained on the
   * contract for back-compat (the deterministic diagram now lives on the sibling
   * sketch card, so the raster card no longer consumes this).
   */
  onFetchLayout?: (viewId: string) => Promise<ReactFlowLayoutJson | null>;
  /**
   * Fetch the raw `view.sysml`. Retained on the contract for back-compat (the
   * SysML source now lives on the sibling sketch card).
   */
  onFetchSysmlSource?: (viewId: string) => Promise<string | null>;
}

export interface IllustrativeImagePanelProps
  extends IllustrativeImageEnvelopeData {
  handlers: IllustrativeImagePanelHandlers;
  /** Honour `prefers-reduced-motion: reduce`. Default: auto-detect. */
  reducedMotion?: boolean;
  /** Inject a clock for deterministic elapsed-timer tests. */
  nowMs?: () => number;
}

function useDetectReducedMotion(override: boolean | undefined): boolean {
  const [auto, setAuto] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setAuto(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return override ?? auto;
}

export function IllustrativeImagePanel(
  props: IllustrativeImagePanelProps,
): JSX.Element {
  const { imageViewId, viewId, sceneDescriptionAlt, handlers } = props;
  const card = useStore(handlers.store, selectImageCard(imageViewId));
  const reducedMotion = useDetectReducedMotion(props.reducedMotion);
  // Stabilise the clock. A fresh `() => Date.now()` on every render is a NEW
  // reference each time, and it sits in the elapsed-timer / timeout effect
  // dependency arrays below — so an unstable `nowFn` makes those effects re-run
  // (and re-schedule setState) on EVERY commit, a runaway re-render loop that
  // pegs a CPU core for the whole ~20–120s generation and trips React's
  // "Maximum update depth exceeded". `useMemo` keyed on the (test-only) prop
  // keeps the identity stable across renders.
  const nowMsProp = props.nowMs;
  const nowFn = useMemo<() => number>(
    () => nowMsProp ?? ((): number => Date.now()),
    [nowMsProp],
  );

  const [instruction, setInstruction] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const status = card?.status ?? 'generating';
  const imageUrl = card?.imageUrl;
  const alt = card?.alt ?? sceneDescriptionAlt ?? 'Illustrative diagram';
  // Fallback start time for the (rare) case where the card carries none yet.
  // Capture it once so it doesn't change every render and re-arm the effects.
  const fallbackStartRef = useRef<number | null>(null);
  fallbackStartRef.current ??= nowFn();
  const startedAtMs = card?.startedAtMs ?? fallbackStartRef.current;
  const isGenerating = status === 'generating';

  // ── Elapsed timer (only ticks while generating; reduced-motion freezes it) ──
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  useEffect(() => {
    if (!isGenerating) return undefined;
    if (reducedMotion) {
      setElapsedMs(Math.max(0, nowFn() - startedAtMs));
      return undefined;
    }
    const tick = (): void => setElapsedMs(Math.max(0, nowFn() - startedAtMs));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [isGenerating, reducedMotion, startedAtMs, nowFn]);

  // ── MAJ-3 client-side timeout backstop ──────────────────────────────────
  // If no genReady / genFailed arrives within IMAGE_GEN_TIMEOUT_MS the panel
  // exits the spinner and shows a fallback notice. Resets whenever the card
  // leaves the generating state (a fresh edit re-arms a new window).
  const [timedOut, setTimedOut] = useState<boolean>(false);
  useEffect(() => {
    if (!isGenerating) {
      setTimedOut(false);
      return undefined;
    }
    const remaining = IMAGE_GEN_TIMEOUT_MS - Math.max(0, nowFn() - startedAtMs);
    if (remaining <= 0) {
      setTimedOut(true);
      return undefined;
    }
    setTimedOut(false);
    const id = window.setTimeout(() => setTimedOut(true), remaining);
    return () => window.clearTimeout(id);
  }, [isGenerating, startedAtMs, nowFn]);

  // The spinner only animates while genuinely waiting (not past the backstop).
  const showSpinner = isGenerating && !timedOut;

  const handleCancel = useCallback(() => {
    handlers.store.getState().cancelEdit(imageViewId);
  }, [handlers.store, imageViewId]);

  const doSubmit = useCallback(async () => {
    const trimmed = instruction.trim();
    if (trimmed.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    // MAJ-13 — route by `imageViewId` (the card correlation id) so the
    // image-views slice's `requestEdit` engages the optimistic skeleton +
    // version lineage. The slice POSTs to the card's resolved `viewId`.
    const res = await handlers.onRequestImageEdit(imageViewId, trimmed);
    setSubmitting(false);
    if (res.ok) {
      setInstruction('');
    } else {
      setSubmitError(res.message);
    }
  }, [instruction, handlers, imageViewId]);

  const handleRetry = useCallback(() => {
    void doSubmit();
  }, [doSubmit]);

  const structural = isStructuralChangeRequest(instruction);

  return (
    <CardShell
      role="region"
      ariaLabel="Illustrative diagram"
      cardKind="illustrative-image"
      testId="illustrative-image-card"
      className={reducedMotion ? 'transition-none' : undefined}
    >
      <div className="flex flex-col gap-2">
        {/* Header — names this as the AI illustration stacked below the faithful
            diagram. The deterministic sketch + the SysML source-of-truth live on
            the sibling response-view card ABOVE. */}
        <div
          data-testid="illustrative-image-heading"
          className="text-[11px] font-semibold uppercase tracking-wide text-taupe-700"
        >
          AI visualization
        </div>

        {/* Raster image area. Prior image stays visible under any generating
            overlay so chat is never blocked. */}
        <div
          className="relative overflow-hidden"
          data-testid="illustrative-image-area"
        >
          {imageUrl !== undefined ? (
            <button
              type="button"
              data-testid="illustrative-image-expand"
              aria-label="Expand illustrative image"
              onClick={() => setModalOpen(true)}
              className="block cursor-zoom-in border-0 bg-transparent p-0"
            >
              <img
                data-testid="illustrative-image-img"
                src={imageUrl}
                alt={alt}
                className="block max-h-[260px] max-w-full object-contain"
              />
            </button>
          ) : null}

          {showSpinner ? (
            <GeneratingViewState
              variant={imageUrl !== undefined ? 'overlay' : 'canvas'}
              elapsedMs={elapsedMs}
              onCancel={handleCancel}
              reducedMotion={reducedMotion}
              testIds={{
                root: 'illustrative-image-skeleton',
                elapsed: 'illustrative-image-elapsed',
                cancel: 'illustrative-image-cancel',
              }}
            />
          ) : null}

          {/* MAJ-3 — timeout backstop. No spinner; the deterministic image (if
              any) stays visible underneath and the user can retry. */}
          {isGenerating && timedOut ? (
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="illustrative-image-timeout"
              className={`${
                imageUrl !== undefined
                  ? 'absolute inset-0 bg-cream-50/85'
                  : 'bg-cream-200 border border-taupe-300'
              } flex flex-col items-center justify-center gap-1 rounded-none`}
              style={
                imageUrl === undefined
                  ? {
                      // Match the canvas skeleton's frame exactly so the swap
                      // to the backstop never resizes the slot (no reflow).
                      width: GENERATING_CANVAS_WIDTH,
                      height: GENERATING_CANVAS_HEIGHT,
                    }
                  : undefined
              }
            >
              <span className="text-accent-red text-xs">
                This is taking longer than expected.
              </span>
              <span className="text-[11px] text-taupe-700">
                The image generation may have fallen back. You can retry.
              </span>
              <button
                type="button"
                data-testid="illustrative-image-timeout-retry"
                aria-label="Retry image generation"
                onClick={handleRetry}
                className="border-taupe-500 hover:border-taupe-900 text-fg mt-1 rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              >
                Retry
              </button>
            </div>
          ) : null}
        </div>

        {/* Non-dismissible illustrative badge (ready state). */}
        {status === 'ready' && imageUrl !== undefined ? (
          <span
            data-testid="illustrative-image-badge"
            className="border-amber-500 bg-amber-50 text-amber-900 self-start rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          >
            {ILLUSTRATIVE_BADGE_COPY}
          </span>
        ) : null}

        {/* Filtered / declined-edit message (distinct from a silent fallback). */}
        {status === 'filtered' ? (
          <div
            role="status"
            aria-live="polite"
            data-testid="illustrative-image-declined"
            className="text-accent-red text-xs"
          >
            The model declined this edit. The prior image is retained.
          </div>
        ) : null}
        {status === 'error' ? (
          <div
            role="status"
            aria-live="polite"
            data-testid="illustrative-image-error"
            className="text-accent-red text-xs"
          >
            Image generation failed{card?.errorCode ? ` (${card.errorCode})` : ''}. The
            prior image is retained.
          </div>
        ) : null}
        {status === 'error' || status === 'filtered' ? (
          <button
            type="button"
            data-testid="illustrative-image-retry"
            aria-label="Retry image edit"
            onClick={handleRetry}
            className="border-taupe-500 hover:border-taupe-900 text-fg self-start rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          >
            Retry
          </button>
        ) : null}

        {/* Stale marker. */}
        {card?.stale === true ? (
          <span
            data-testid="illustrative-image-stale"
            aria-label="This image is stale — the model changed since it was generated"
            className="border-taupe-500 text-taupe-700 self-start rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          >
            Stale
          </span>
        ) : null}

        {/* Versioned iterate-history strip (one lineage). */}
        {card !== undefined && card.history.length > 0 ? (
          <div
            data-testid="illustrative-image-history"
            aria-label="Image version history"
            className="flex flex-wrap gap-1"
          >
            {card.history.map((h) => (
              <span
                key={h.version}
                data-testid={`illustrative-image-version-${String(h.version)}`}
                className="border-taupe-300 text-taupe-700 rounded-none border px-1.5 text-[10px]"
              >
                v{h.version}
              </span>
            ))}
          </div>
        ) : null}

        {/* NL change box — cost-first, explicit-invoke-only (never auto-fires). */}
        <form
          data-testid="illustrative-image-nl-form"
          onSubmit={(e) => {
            e.preventDefault();
            void doSubmit();
          }}
          className="flex flex-col gap-1"
        >
          <label
            htmlFor={`illustrative-image-nl-${imageViewId}`}
            className="text-[11px] font-semibold uppercase tracking-wide text-taupe-700"
          >
            Change the diagram
          </label>
          <textarea
            id={`illustrative-image-nl-${imageViewId}`}
            data-testid="illustrative-image-nl-input"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={2}
            placeholder="e.g. realign the blocks, color the battery red, group the sensors"
            className="border-taupe-500 bg-cream-50 text-fg rounded-none border px-2 py-1 text-xs"
          />
          <div className="flex items-center justify-between gap-2">
            <span
              data-testid="illustrative-image-cost"
              className="text-[10px] text-taupe-700"
            >
              ~${IMAGE_EDIT_COST_ESTIMATE_USD.toFixed(2)} · {IMAGE_EDIT_LATENCY_HINT}
            </span>
            <button
              type="submit"
              data-testid="illustrative-image-submit"
              aria-label="Submit diagram change request"
              disabled={submitting || instruction.trim().length === 0}
              className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
          {structural ? (
            <div
              data-testid="illustrative-image-structural-nudge"
              role="note"
              className="text-amber-900 text-[10px]"
            >
              Structural changes (adding/removing parts, ports or connections)
              should go through propose_kb_changeset — this box restyles the
              picture only.
            </div>
          ) : null}
          {submitError !== null ? (
            <div
              role="status"
              aria-live="polite"
              data-testid="illustrative-image-submit-error"
              className="text-accent-red text-[10px]"
            >
              {submitError}
            </div>
          ) : null}
        </form>
      </div>

      {/* AI-image focused view. Clicking the raster opens the full-viewport
          light-frosted view; its bottom prompt pill routes NL edits through the
          same `onRequestImageEdit` path as the in-card change box (keyed by
          `imageViewId` so the optimistic skeleton + version lineage engage). The
          produced image resolves via `image:genReady` and flows back into this
          panel's `imageUrl`, so the focused view updates in place. */}
      <ViewImageModal
        open={modalOpen}
        layout={null}
        viewId={viewId}
        viewName={alt}
        onClose={() => setModalOpen(false)}
        onEditRequest={(request) =>
          handlers.onRequestImageEdit(imageViewId, request)
        }
        {...(imageUrl !== undefined ? { imageSrc: imageUrl, imageAlt: alt } : {})}
      />
    </CardShell>
  );
}
